import React, { useState } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  useToast,
  useColorModeValue,
  Icon,
  SimpleGrid,
  Link,
} from "@chakra-ui/react";
import { hitBeApi } from "../../api/api";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { FiSend } from "react-icons/fi";
import { LegalPageShell } from "./legal/LegalPageShell";
import PageSeo from "../../seo/PageSeo";
import { DEFAULT_KEYWORDS, SITE_URL } from "../../seo/siteConfig";
import { breadcrumbSchema } from "../../seo/structuredData";

const ContactUs = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const inputBg = useColorModeValue("gray.50", "gray.900");
  const muted = useColorModeValue("gray.600", "gray.400");
  const sidebarHeadingColor = useColorModeValue("teal.700", "teal.300");
  const formBg = useColorModeValue("gray.50", "gray.900");
  const formBorder = useColorModeValue("gray.100", "gray.700");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    const formData = new FormData(event.target);
    const data = {
      name: formData.get("name"),
      email: formData.get("email"),
      phoneNumber: formData.get("phoneNumber"),
      message: formData.get("message"),
    };

    try {
      const response = await hitBeApi("contact-us", "POST", data);
      if (response.success) {
        toast({
          title: "Message sent",
          description: "Thanks for reaching out—we will reply as soon as we can.",
          status: "success",
          duration: 6000,
          isClosable: true,
        });
        event.target.reset();
        navigate("/");
      } else {
        toast({
          title: "Something went wrong",
          description: response?.message || "Please try again in a moment.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Could not send",
        description: error.message,
        status: "error",
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const jsonLd = breadcrumbSchema([
    { name: "Home", url: SITE_URL },
    { name: "Contact", url: `${SITE_URL}/contact-us` },
  ]);

  return (
    <>
      <PageSeo
        title="Contact Manage360 — support, privacy, export requests"
        description="Contact Manage360 for product questions, account help, data export, or deletion requests. We respond by email."
        keywords={`${DEFAULT_KEYWORDS}, contact Manage360, support`}
        canonicalPath="/contact-us"
        jsonLd={jsonLd}
      />
    <LegalPageShell
      badge="Support"
      title="Contact"
      intro="Send a message and we’ll get back to you."
      maxW="min(100%, 960px)"
    >
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 10, lg: 14 }} alignItems="start">
        <VStack align="stretch" spacing={6}>
          <Box>
            <Heading as="h2" size="sm" color={sidebarHeadingColor} mb={3}>
              Before you write
            </Heading>
            <Text color={muted} lineHeight="tall" fontSize="md">
              Check{" "}
              <Link as={RouterLink} to="/faqs" color="teal.500" fontWeight="semibold">
                FAQs
              </Link>
              {" · "}
              <Link as={RouterLink} to="/privacy-policy" color="teal.500" fontWeight="semibold">
                Privacy
              </Link>
              {" · "}
              <Link as={RouterLink} to="/terms-and-conditions" color="teal.500" fontWeight="semibold">
                Terms
              </Link>
            </Text>
            <Text color={muted} fontSize="sm" mt={4} lineHeight="tall">
              Include your account email or phone and what went wrong. We usually reply within a few business days.
            </Text>
          </Box>
        </VStack>

        <Box
          as="form"
          onSubmit={handleSubmit}
          bg={formBg}
          borderRadius="2xl"
          borderWidth="1px"
          borderColor={formBorder}
          p={{ base: 6, md: 8 }}
        >
          <VStack spacing={5} align="stretch">
            <FormControl id="name" isRequired>
              <FormLabel fontWeight="semibold">Name</FormLabel>
              <Input
                name="name"
                placeholder="How we should address you"
                bg={inputBg}
                borderRadius="lg"
                focusBorderColor="teal.500"
                size="lg"
              />
            </FormControl>
            <FormControl id="email" isRequired>
              <FormLabel fontWeight="semibold">Email</FormLabel>
              <Input
                name="email"
                type="email"
                placeholder="you@example.com"
                bg={inputBg}
                borderRadius="lg"
                focusBorderColor="teal.500"
                size="lg"
              />
            </FormControl>
            <FormControl id="phoneNumber">
              <FormLabel fontWeight="semibold">Phone (optional)</FormLabel>
              <Input
                name="phoneNumber"
                type="tel"
                placeholder="+91 … or your country code"
                bg={inputBg}
                borderRadius="lg"
                focusBorderColor="teal.500"
                size="lg"
              />
            </FormControl>
            <FormControl id="message" isRequired>
              <FormLabel fontWeight="semibold">Message</FormLabel>
              <Textarea
                name="message"
                placeholder="What happened?"
                rows={6}
                bg={inputBg}
                borderRadius="lg"
                focusBorderColor="teal.500"
                resize="vertical"
              />
            </FormControl>
            <Button
              type="submit"
              colorScheme="teal"
              size="lg"
              rounded="xl"
              h="54px"
              fontWeight="bold"
              rightIcon={<Icon as={FiSend} />}
              isLoading={submitting}
              loadingText="Sending"
              bgGradient="linear(to-r, teal.500, teal.600)"
              _hover={{
                bgGradient: "linear(to-r, teal.600, teal.700)",
                transform: "translateY(-1px)",
                boxShadow: "md",
              }}
            >
              Send message
            </Button>
          </VStack>
        </Box>
      </SimpleGrid>
    </LegalPageShell>
    </>
  );
};

export default ContactUs;
